import React from 'react';
import { Icon, Spinner } from '@blueprintjs/core';

// Project search list, shared by components/ProjectAutocomplete (timesheet
// filter) and components/Job/ProjectAutocomplete (job form). Same look as the
// client and location searches: a status line, then one row per project with
// its number, status, dates and PO.
// Styles: .client-suggest-* and .project-suggest-* in styles/search-filters-bar.scss.

const statusLabel = (a) =>
  a.status_formatted || String(a.status || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// Number, status, dates and PO: what tells two projects apart at a glance.
// `compact`: number and status only (the picked project, in a narrow field).
export const ProjectDetails = ({ project, showClient = true, compact = false }) => {
  const a = (project && project.attributes) || {};
  const dates = a.start_date_formatted && a.end_date_formatted
    ? `${a.start_date_formatted} – ${a.end_date_formatted}`
    : null;
  return (
    <span className={`project-suggest-details${compact ? ' is-compact' : ''}`}>
      {a.number && <span className="project-suggest-number">#{a.number}</span>}
      {a.status && (
        <span className={`project-suggest-status is-${String(a.status).replace(/[^a-z_]/gi, '')}`}>{statusLabel(a)}</span>
      )}
      {!compact && showClient && a.client_name && <span>{a.client_name}</span>}
      {!compact && dates && <span>{dates}</span>}
      {!compact && a.po_number && <span>PO {a.po_number}</span>}
    </span>
  );
};

// One row per project. `projectRenderer({ showClient: false })` leaves the
// client out where the list is already one client's projects.
export const projectRenderer = ({ showClient = true } = {}) => (project, { handleClick, modifiers }) => {
  const a = (project && project.attributes) || {};
  return (
    <li key={project.id}>
      <div
        role="option"
        aria-selected={modifiers.active}
        className={`client-suggest-option project-suggest-option${modifiers.active ? ' is-active' : ''}`}
        onClick={handleClick}
      >
        <span className="client-suggest-avatar project-suggest-icon" aria-hidden="true"><Icon icon="projects" iconSize={14} /></span>
        <span className="client-suggest-text">
          <span className="client-suggest-name">{a.name || 'Untitled project'}</span>
          <ProjectDetails project={project} showClient={showClient} />
        </span>
      </div>
    </li>
  );
};

export const ProjectRenderer = projectRenderer();

const plural = (count) => (count === 1 ? 'project' : 'projects');

export const ProjectListRenderer = ({ items, itemsParentRef, query, renderItem, loading, failed }) => {
  const renderedItems = items.map(renderItem).filter((item) => item != null);
  let status;
  if (failed && !loading) {
    status = 'Couldn’t load projects. Try again.';
  } else if (loading) {
    status = <span className="client-suggest-loading"><Spinner size={14} /> Searching projects…</span>;
  } else if (renderedItems.length === 0) {
    status = query ? `No projects match “${query}”` : 'No projects yet';
  } else {
    status = query
      ? `${renderedItems.length} ${plural(renderedItems.length)} ${renderedItems.length === 1 ? 'matches' : 'match'} “${query}”`
      : `${renderedItems.length} ${plural(renderedItems.length)}`;
  }
  return (
    <div className="client-suggest-menu">
      <div className="client-suggest-status">{status}</div>
      {!loading && !failed && renderedItems.length > 0 && (
        <ul className="client-suggest-list" ref={itemsParentRef} role="listbox">
          {renderedItems}
        </ul>
      )}
    </div>
  );
};
